import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { User, Calendar, Clock, AlertTriangle, Archive, Trash2, MoreVertical, Briefcase, MessageCircle, Settings, Mic } from "lucide-react";
import { Link } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface Assessment {
  id: string;
  title: string;
  skills: string[];
  candidatesInvited: number;
  candidatesCompleted: number;
  candidatesInProgress: number;
  date: string;
  status: 'draft' | 'active' | 'completed' | 'inactive' | 'archived';
  level?: string;
  created_at?: string;
  source?: 'RoundOne' | 'ATS';
  ats_job_id?: string;
  custom_questions?: string[];
  skill_questions?: Record<string, string[]>;
  language?: string;
  accent?: string;
  voice_id?: string;
}

interface AssessmentCardProps {
  assessment: Assessment;
  className?: string;
  onClick?: () => void;
  onDelete?: () => void;
  onArchive?: () => void;
}

const AssessmentCard: React.FC<AssessmentCardProps> = ({ 
  assessment, 
  className = '', 
  onClick,
  onDelete,
  onArchive
}) => {
  const { title, skills, candidatesInvited, candidatesCompleted, candidatesInProgress, date, status, source, custom_questions, skill_questions, language, accent, voice_id } = assessment;
  const progress = candidatesInvited > 0 ? (candidatesCompleted / candidatesInvited) * 100 : 0;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);

  const statusColors = {
    draft: 'bg-slate-400/10 text-slate-400',
    active: 'bg-green-400/10 text-green-400',
    completed: 'bg-blue-400/10 text-blue-400',
    inactive: 'bg-yellow-400/10 text-yellow-400',
    archived: 'bg-gray-400/10 text-gray-400',
  };

  // Calculate total questions count
  const generalQuestionsCount = custom_questions?.length || 0;
  const skillQuestionsCount = skill_questions 
    ? Object.values(skill_questions).reduce((sum, questions) => sum + (Array.isArray(questions) ? questions.length : 0), 0) 
    : 0;
  const totalQuestionsCount = generalQuestionsCount + skillQuestionsCount;

  // Check if interview settings are configured
  const hasVoiceSettings = !!voice_id;

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('assessments')
        .delete()
        .eq('id', assessment.id);

      if (error) throw error;

      toast.success('Assessment deleted successfully');
      onDelete?.();
    } catch (error) {
      console.error('Error deleting assessment:', error);
      toast.error('Failed to delete assessment');
    }
    setShowDeleteDialog(false);
  };

  const handleArchive = async () => {
    try {
      const { error } = await supabase
        .from('assessments')
        .update({ status: 'archived' })
        .eq('id', assessment.id);

      if (error) throw error;

      toast.success('Assessment archived successfully');
      onArchive?.();
    } catch (error) {
      console.error('Error archiving assessment:', error);
      toast.error('Failed to archive assessment');
    }
    setShowArchiveDialog(false);
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Prevent card click when clicking on the dropdown menu
    if ((e.target as HTMLElement).closest('.dropdown-trigger')) {
      e.stopPropagation();
      return;
    }
    onClick?.();
  };

  return (
    <>
      <Card 
        className={`glass-card cursor-pointer ${status === 'inactive' ? 'border-2 border-yellow-300' : ''} ${className}`}
        onClick={handleCardClick}
      >
        <CardHeader className="pb-2">
          <div className="flex justify-between items-start">
            <CardTitle className="text-lg line-clamp-1">{title}</CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className={statusColors[status]}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </Badge>
              <div className="relative" onClick={e => e.stopPropagation()}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="h-8 w-8 p-0 hover:bg-slate-100 focus:ring-2 focus:ring-slate-200"
                    >
                      <MoreVertical className="h-5 w-5 text-slate-600" />
                      <span className="sr-only">Open menu</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-[160px]">
                    {candidatesInvited === 0 ? (
                      <DropdownMenuItem 
                        className="text-red-600 cursor-pointer focus:text-red-600 focus:bg-red-50"
                        onClick={() => setShowDeleteDialog(true)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => setShowArchiveDialog(true)}
                      >
                        <Archive className="mr-2 h-4 w-4" />
                        Archive
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
          <CardDescription className="flex flex-wrap items-center gap-2">
            <span className="flex items-center">
              <Calendar size={14} className="text-muted-foreground mr-1" />
              {date}
            </span>
            {source && (
              <Badge variant="outline" className={source === 'ATS' ? 'bg-blue-50 text-blue-600' : 'bg-card'}>
                <Briefcase className="w-3 h-3 mr-1" />
                {source}
              </Badge>
            )}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <div className="space-y-4">
            {/* Skills display */}
            <div className="flex flex-wrap gap-2">
              {skills.map((skill, index) => (
                <Badge key={index} variant="outline" className="bg-card">
                  {skill}
                </Badge>
              ))}
            </div>
            
            {/* Question counts */}
            {totalQuestionsCount > 0 && (
              <div className="flex items-center space-x-3 text-sm text-muted-foreground">
                <div className="flex items-center">
                  <MessageCircle className="w-3.5 h-3.5 mr-1" />
                  <span>{totalQuestionsCount} Questions</span>
                </div>
                {skillQuestionsCount > 0 && (
                  <div className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                    {Object.keys(skill_questions || {}).length} Skills with Questions
                  </div>
                )}
              </div>
            )}
            
            {/* Voice Settings */}
            {hasVoiceSettings && (
              <div className="flex items-center text-xs font-medium text-purple-600 bg-purple-50 px-2 py-1 rounded">
                <Mic className="w-3 h-3 mr-1" />
                <span>Voice: {language?.toUpperCase()}-{accent?.toUpperCase()}</span>
              </div>
            )}
            
            {/* Candidate Progress */}
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-muted-foreground">Completion Rate</span>
                <span>{candidatesCompleted} of {candidatesInvited} completed</span>
              </div>
              <Progress value={progress} className="h-2" variant="gradient" />
            </div>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Assessment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this assessment? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Assessment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to archive this assessment? You can still access it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive}>
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AssessmentCard;
